import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Check, CheckCheck, MessageSquare, ThumbsUp, AlertTriangle, X } from 'lucide-react';

const API = '/api';

const TYPE_ICON = {
  content_approved: ThumbsUp,
  changes_requested: AlertTriangle,
  new_comment: MessageSquare,
  content_rejected: X,
};

const TYPE_COLOR = {
  content_approved: 'text-emerald-600',
  changes_requested: 'text-amber-600',
  new_comment: 'text-blue-600',
  content_rejected: 'text-red-600',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/social/notifications`, { withCredentials: true });
      setData(d);
    } catch {}
  }, []);

  useEffect(() => { fetch(); const i = setInterval(fetch, 30000); return () => clearInterval(i); }, [fetch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/social/notifications/mark-read`, {}, { withCredentials: true });
      setData(prev => ({
        ...prev,
        unread_count: 0,
        notifications: prev.notifications.map(n => ({ ...n, read: true }))
      }));
    } catch {}
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await axios.post(`${API}/social/notifications/mark-read`, { notification_ids: [notif.notification_id] }, { withCredentials: true });
      setData(prev => ({
        ...prev,
        unread_count: Math.max(0, prev.unread_count - 1),
        notifications: prev.notifications.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n)
      }));
    }
    if (notif.content_id) {
      navigate(`/social/content/${notif.content_id}`);
      setOpen(false);
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost" size="icon"
        onClick={() => setOpen(!open)}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {data.unread_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-medium" data-testid="notif-unread-badge">
            {data.unread_count > 9 ? '9+' : data.unread_count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
            {data.unread_count > 0 && (
              <button onClick={markAllRead} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid="mark-all-read-btn">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="text-sm text-slate-400 p-6 text-center">No notifications yet</p>
            ) : (
              data.notifications.map(n => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const color = TYPE_COLOR[n.type] || 'text-slate-500';
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 flex items-start gap-3 ${!n.read ? 'bg-indigo-50/40' : ''}`}
                    data-testid={`notif-item-${n.notification_id}`}
                  >
                    <div className={`mt-0.5 ${color}`}><Icon className="h-4 w-4" /></div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>{n.title}</p>
                      <p className="text-xs text-slate-400 truncate">{n.message}</p>
                      <p className="text-[10px] text-slate-300 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
